// Runtime configuration, resolved once at module load.
//
// window.__ENV__ is written by docker-entrypoint.sh at container start from the
// k8s deployment env, so the same image serves every environment. process.env is
// the Parcel build-time fallback (baked in via the Dockerfile ARG) for local dev.

const runtime = (typeof window !== 'undefined' && window.__ENV__) || {};

// The GraphQL endpoint of parsina.bff.dentist (:4004), read by src/lib/bffClient.js.
//
// The BFF mounts Apollo at `/` (parsina.bff.dentist/src/index.js), and express's
// `app.use('/')` is a catch-all, so the `/graphql` suffix is accepted as well as
// the bare origin. The suffix is kept because the doctor and clinic clients both
// spell their BFF_URL that way — one shape for the variable is worth more than a
// byte saved.
//
// The default below is the STAGING host (staging = dapi.parsinabot.ir, beta1 =
// beta1.dapi.parsinabot.ir). The deployment is expected to set BFF_URL per
// environment; a beta1 pod silently reading staging's data would be far worse
// than one that fails visibly.
export const BFF_URL =
  runtime.BFF_URL || process.env.BFF_URL || 'https://dapi.parsinabot.ir/graphql';

// The WebSocket origin of the same BFF, for `/ws/transcribe` (voice dictation).
//
// DERIVED from BFF_URL rather than configured beside it: the socket is served by
// the very same process on the very same host, so two variables would only ever
// be two chances to point them at different environments. `BFF_WS_URL` stays as
// an override for the one case the derivation cannot cover — a deployment that
// terminates WebSockets somewhere else.
//
// The two replaces run https-first and the order is load-bearing: `^http`
// matches the start of "https" as well, so running it first would rewrite
// `https://` to `wss://` and leave the second replace with nothing to do — the
// right answer by accident for https, and no answer at all for a plain `http://`
// dev BFF. Same expression the doctor and clinic clients use.
export const BFF_WS_BASE =
  runtime.BFF_WS_URL ||
  process.env.BFF_WS_URL ||
  BFF_URL.replace(/^https/, 'wss').replace(/^http/, 'ws').replace('/graphql', '');

// Which engine turns the dentist's voice into text.
//
//   false (DEFAULT) → the BFF socket: MediaRecorder streams to /ws/transcribe
//                     and Parsina's own AI service transcribes it.
//   true            → the browser's SpeechRecognition, as it was before
//                     2026-08-25.
//
// THE DEFAULT IS THE SERVER, and the reason is Safari. `webkitSpeechRecognition`
// does not exist on Safari or in ANY browser on iOS, so the browser engine hid
// the microphone outright on an iPad — the device a dentist holds chairside.
// MediaRecorder exists everywhere.
//
// Two further consequences of the default, neither of them incidental:
//   * PRIVACY IMPROVES. Chrome and Edge implement SpeechRecognition by streaming
//     the microphone to GOOGLE'S servers. The patient's spoken complaint now
//     goes to Parsina's own service instead.
//   * IT COSTS پارسه. The AI service bills per transcribed minute where the
//     browser engine was free. That is what this switch is for: a deployment
//     that would rather not pay can set USE_BROWSER_STT=true and get the old
//     behaviour back on Chrome — and no microphone at all on iPads, which is
//     the trade it is choosing.
export const USE_BROWSER_STT =
  String(runtime.USE_BROWSER_STT ?? process.env.USE_BROWSER_STT ?? 'false') === 'true';

// Keycloak origin, no realm path — auth.js appends /realms/<realm>. Routed
// through here (rather than a bare constant in auth.js) so the deployment can
// move the SSO host without a client rebuild, same as the sibling clients.
export const KC_BASE_URL =
  runtime.PARSINA_KC_BASE_URL ||
  process.env.PARSINA_KC_BASE_URL ||
  'https://sso.itechtrade.ir';

// Origin of حساب پارسینا — where a person edits their own identity, password,
// language and two-factor, for every panel at once. Supplied per environment by
// the deployment, exactly like BFF_URL, rather than baked into the image: a
// beta1 panel linking to the staging account app would hand someone a session
// against another environment's data.
//
// Like the clinic switch, this is a plain navigation and not a token hand-off —
// same realm, so the account app runs its own PKCE flow and Keycloak's SSO
// session returns a token without a second sign-in.
//
// No trailing slash — callers append the path.
export const ACCOUNT_APP_URL = (
  runtime.ACCOUNT_APP_URL ||
  process.env.ACCOUNT_APP_URL ||
  'https://account.parsinabot.ir'
).replace(/\/+$/, '');

/**
 * A link into the account app that can find its way back here.
 *
 * `return_url` is checked SERVER-SIDE by the account app against its own
 * allowlist; this origin is already on it for both environments. An unlisted
 * host does not error — the «بازگشت» chip simply does not render — so a new
 * panel hostname needs adding there before its link is useful.
 */
export function accountUrl(section = '/') {
  const back = typeof window !== 'undefined' ? window.location.origin : '';
  const path = section.startsWith('/') ? section : `/${section}`;
  return `${ACCOUNT_APP_URL}${path}${back ? `?return_url=${encodeURIComponent(back)}` : ''}`;
}

// Origin of «پنل کلینیک». Same shape and the same defaults as the doctor
// client's copy (doctor-app/parsina.client.web/src/lib/runtimeEnv.js), because a
// dentist who runs their own clinic reaches the SAME panel the doctor app
// reaches — the dentist app connects to the clinic through the shared services,
// exactly as the doctor app does, and never app-to-app.
//
// A plain navigation, not a token hand-off: both panels use the same Keycloak
// client on the same realm, so the clinic app runs its own PKCE flow and the
// live SSO session returns a token without a second sign-in.
//
// No trailing slash — callers append the path.
export const CLINIC_APP_URL = (
  runtime.CLINIC_APP_URL ||
  process.env.CLINIC_APP_URL ||
  'https://clinic.parsinabot.ir'
).replace(/\/+$/, '');

/**
 * Where «رفتن به پنل کلینیک» goes.
 *
 * THE TARGET IS NAMED IN THE PATH. The clinic panel routes on `/c/:ref/…`, so a
 * dentist scoped to one of several clinics lands on the one they were working
 * in rather than on whichever the panel's own inference ladder returns.
 * With no clinic it goes to the root, which is the honest thing to do when
 * nothing was chosen.
 */
export function clinicPanelUrl(clinic) {
  const ref = typeof clinic === 'string' ? clinic : (clinic?.slug || clinic?.clinicId || clinic?.id || '');
  return ref ? `${CLINIC_APP_URL}/c/${encodeURIComponent(ref)}/` : `${CLINIC_APP_URL}/`;
}

/**
 * The one place this app leaves for the clinic panel — a function rather than an
 * inline `window.location.assign` at each call site, so leaving is observable in
 * tests, where jsdom's `location` cannot be replaced.
 */
export function enterClinicPanel(clinic) {
  window.location.assign(clinicPanelUrl(clinic));
}
